/// Vue Router 4 config — Web Tenant admin 4 màn hình (spec 2026-06).
///
/// Routes:
///   /login                           — public
///   /terms, /privacy                 — 利用規約 / プライバシーポリシー (public)
///   /organization                    — Tổ chức (default landing post-login)
///   /map-tracking                    — Map & Tracking
///   /scenario-sets                   — Alert Scenario Sets (Corp Admin)
///   /scenario-template               — Scenario master template (Super Admin)
///   /audit-log                       — Audit Log (System Settings group)
///
/// Old Phase 3.3 routes (/alerts, /alerts/:id) removed — Alert management
/// moved to Mobile per spec 2026-06.
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/features/auth/stores/auth-store'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/pages/LoginPage.vue'),
    meta: { public: true },
  },
  {
    // Kích hoạt tài khoản qua link trong email mời (token ở query).
    path: '/activate',
    name: 'activate',
    component: () => import('@/features/auth/pages/ActivatePage.vue'),
    meta: { public: true },
  },
  {
    // Request a password reset email (enter email). Public.
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/features/auth/pages/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    // Đặt lại mật khẩu qua link trong email quên mật khẩu (token ở query).
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/features/auth/pages/ResetPasswordPage.vue'),
    meta: { public: true },
  },
  {
    // Landing sau khi end-user (dept_manager/employee) active tài khoản: họ không
    // dùng web admin → chỉ dẫn tải app theo OS. Public (không cần login).
    path: '/deeplink/redirect-to-login',
    name: 'deeplink-redirect',
    component: () => import('@/features/auth/pages/DeeplinkRedirectPage.vue'),
    meta: { public: true },
  },
  {
    // Đích của link trong mail nhắc/quá hạn mượn tài sản dùng chung
    // (`/shared-assets?assetId=…`). Trên mobile, link này mở APP (deep link theo host)
    // vào màn 共用資産; mượn/trả chỉ có trên App (spec §131) nên Web không có màn này —
    // mở trên desktop thì hướng dẫn tải app thay vì rơi vào trang 404.
    path: '/shared-assets',
    name: 'shared-assets-deeplink',
    component: () => import('@/features/auth/pages/DeeplinkRedirectPage.vue'),
    props: { subtitleKey: 'deeplinkSharedAssetSubtitle' },
    meta: { public: true },
  },
  {
    // 利用規約 — văn bản pháp lý công khai. Public vì phải xem được TRƯỚC khi đăng
    // nhập (chân trang login) và trước khi tick đồng ý ở màn kích hoạt tài khoản.
    path: '/terms',
    name: 'terms-of-use',
    component: () => import('@/features/legal/pages/TermsOfUsePage.vue'),
    meta: { public: true },
  },
  {
    // プライバシーポリシー — public, cùng lý do với /terms. URL này cũng dùng làm
    // privacy-policy URL khai báo cho App Store / Google Play của app mobile.
    path: '/privacy',
    name: 'privacy-policy',
    component: () => import('@/features/legal/pages/PrivacyPolicyPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    redirect: '/organization',
  },
  {
    // SCR-RPT-SAD-001 Bảng Tổng quan — landing của TTS Super Admin (tenantId = null).
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/features/super-admin-dashboard/pages/SuperAdminDashboardPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-01 Quản lý công ty — chỉ TTS Super Admin (tenantId = null).
    path: '/companies',
    name: 'companies',
    component: () => import('@/features/company/pages/CompanyPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-RPT-CAD-001 Bảng Tổng quan — landing của Corp Admin (tenant-scoped).
    path: '/corp-dashboard',
    name: 'corp-dashboard',
    component: () => import('@/features/corp-admin-dashboard/pages/CorpAdminDashboardPage.vue'),
    meta: { corpAdmin: true },
  },
  {
    path: '/organization',
    name: 'organization',
    component: () => import('@/features/organization/pages/OrganizationPage.vue'),
  },
  {
    // SCR-USR-TCA-001 Tenant Corp Admin management — Corp Admin (tenant-scoped).
    path: '/tenant-corp-admins',
    name: 'tenant-corp-admins',
    component: () => import('@/features/tenant-corp-admin/pages/TenantCorpAdminPage.vue'),
    meta: { corpAdmin: true },
  },
  {
    // SCR-USR-001 Quản lý Corp Admin — chỉ TTS Super Admin (cross-tenant).
    path: '/corp-admins',
    name: 'corp-admins',
    component: () => import('@/features/corp-admin/pages/CorpAdminPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-TTS-OV-ACCOUNT Giám sát TTS Account — chỉ Super Admin (cross-tenant).
    path: '/oversight/tts-accounts',
    name: 'tts-account-oversight',
    component: () => import('@/features/tts-oversight/pages/TtsAccountOversightPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-TTS-OV-TAG Giám sát Thẻ — chỉ Super Admin (cross-tenant).
    path: '/oversight/tags',
    name: 'tag-oversight',
    component: () => import('@/features/tts-oversight/pages/TagOversightPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-OBS-EM-001 Giám sát Lỗi — chỉ Super Admin.
    path: '/monitoring/errors',
    name: 'error-monitoring',
    component: () => import('@/features/error-monitoring/pages/ErrorMonitoringPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-ALT-DIAG-001 Alert diagnostics — Super Admin only. Deliberately NOT in the
    // sidebar: an internal support tool reached by URL, and the backend also 404s
    // unless ALERT_EXPLAIN_ENABLED is set for the environment.
    path: '/monitoring/alert-diagnostics',
    name: 'alert-diagnostics',
    component: () => import('@/features/alert-diagnostics/pages/AlertDiagnosticsPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-USR-POSHIST-001 従業員位置情報履歴 — Super Admin only. Like alert-diagnostics
    // it is deliberately NOT in the sidebar: an internal TTS tool reached by URL, and
    // the backend also 404s unless USER_POSITION_HISTORY_ENABLED is set for the
    // environment (it reads personal location data, APPI scope).
    path: '/monitoring/user-position-history',
    name: 'user-position-history',
    component: () =>
      import('@/features/user-position-history/pages/UserPositionHistoryPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-SYS-GLOBAL-001 グローバル設定 — gộp System Settings + outside-business-hours defaults.
    // Chỉ Super Admin (System Settings sidebar group). Features SYS-map-provider + SYS-private-time.
    path: '/global-setting',
    name: 'global-setting',
    component: () => import('@/features/settings/pages/GlobalSettingPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // SCR-TTS-003 Quản lý Thẻ — Corp Admin (tenant-scoped).
    path: '/card-management',
    name: 'card-management',
    component: () => import('@/features/card-management/pages/CardManagementPage.vue'),
  },
  {
    // SCR-05 Quản lý tài sản — Corp Admin (tenant-scoped).
    // Path tránh '/assets' vì trùng thư mục build của Vite (dist/assets) → F5 ra 403.
    path: '/asset-management',
    name: 'assets',
    component: () => import('@/features/asset/pages/AssetPage.vue'),
  },
  {
    // SCR-AST-HIST-001 Borrow/Return history — Corp Admin (AST-shared-loan).
    path: '/loan-history',
    name: 'loan-history',
    component: () => import('@/features/asset/pages/LoanHistoryPage.vue'),
  },
  {
    // SCR-ZON-001 (W32/W33) Zone management — Corp Admin (tenant-scoped).
    path: '/zones',
    name: 'zones',
    component: () => import('@/features/zone/pages/ZonePage.vue'),
  },
  {
    path: '/map-tracking',
    name: 'map-tracking',
    component: () => import('@/features/map/pages/MapTrackingPage.vue'),
  },
  {
    // ALT-scenario-sets: Scenario Sets — Corp Admin (tenant-scoped). Replaces
    // /alert-scenario (combined-rule config removed).
    path: '/scenario-sets',
    name: 'scenario-sets',
    meta: { corpAdmin: true },
    component: () => import('@/features/scenario-set/pages/ScenarioSetListPage.vue'),
  },
  {
    // ALT-scenario-sets: master template editor — Super Admin only.
    path: '/scenario-template',
    name: 'scenario-template',
    meta: { superAdmin: true },
    component: () => import('@/features/scenario-set/pages/ScenarioTemplatePage.vue'),
  },
  {
    // ALT-history: Alert History — Corp Admin (tenant-scoped).
    path: '/alert-history',
    name: 'alert-history',
    component: () => import('@/features/alert/pages/AlertHistoryPage.vue'),
  },
  {
    // AST-tag-history: Tag Assignment History — Corp Admin (tenant-scoped).
    path: '/tag-history',
    name: 'tag-history',
    component: () => import('@/features/tag/pages/TagHistoryPage.vue'),
  },
  {
    // AST-lost-report: Lost-Asset History — Corp Admin (tenant-scoped). Corp-admin-only
    // in practice (Web has only super_admin/corp_admin; super_admin blocked via
    // TENANT_ADMIN_ROUTES) + backend asset-loss-history:read gate. Mirrors loan-history/
    // tag-history (no meta.corpAdmin — avoids the async-ME race on hard navigation).
    path: '/lost-asset-history',
    name: 'lost-asset-history',
    component: () => import('@/features/asset/pages/LostAssetHistoryPage.vue'),
  },
  {
    // USR-importasync: CSV Import History (user / organization / asset) — Corp Admin
    // (tenant-scoped). Same pattern as lost-asset-history: no meta.corpAdmin (avoids
    // the async-ME race); super_admin blocked via TENANT_ADMIN_ROUTES + the backend
    // import-permission gate.
    path: '/import-history',
    name: 'import-history',
    component: () => import('@/features/import/pages/ImportHistoryPage.vue'),
  },
  {
    // Asset categories — used by BOTH Super Admin (master/global) and Corp Admin
    // (tenant-own) → no meta.superAdmin and NOT in TENANT_ADMIN_ROUTES
    // (same as 'audit-log').
    path: '/categories',
    name: 'categories',
    component: () => import('@/features/settings/pages/CategoryPage.vue'),
  },
  {
    // SYS-tag-model: システム設定 > BLEトラッカーモデル — model config (capabilities,
    // TTS SKU prefixes). Super Admin only: models are TTS master data.
    path: '/tag-models',
    name: 'tag-models',
    component: () => import('@/features/settings/pages/TagModelPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // システム設定 > 監査ログ — leaf of the System Settings sidebar group.
    path: '/audit-log',
    name: 'audit-log',
    component: () => import('@/features/settings/pages/AuditLogPage.vue'),
  },
  // セキュリティ設定 (Security Settings) group — Corp Admin (tenant-scoped).
  // Split out of the old Settings > Security tab into 3 sidebar menu items.
  {
    path: '/security/private-time',
    name: 'security-private-time',
    component: () => import('@/features/settings/pages/PrivateTimePage.vue'),
    meta: { corpAdmin: true },
  },
  {
    path: '/security/ip-restriction',
    name: 'security-ip-restriction',
    component: () => import('@/features/settings/pages/IpRestrictionPage.vue'),
    meta: { corpAdmin: true },
  },
  {
    path: '/security/device-restriction',
    name: 'security-device-restriction',
    component: () => import('@/features/settings/pages/DeviceRestrictionPage.vue'),
    meta: { corpAdmin: true },
  },
  {
    // My Page — self-service (profile + ngôn ngữ + đổi mật khẩu). Mọi user đã
    // đăng nhập, kể cả Super Admin → KHÔNG nằm trong TENANT_ADMIN_ROUTES.
    path: '/my-page',
    name: 'my-page',
    component: () => import('@/features/auth/pages/MyPage.vue'),
  },
  // Onboarding guide — "what is this system and what do I do first". Open to every
  // signed-in admin (both roles) and absent from TENANT_ADMIN_ROUTES for the same
  // reason as /faq: it is product help, not tenant data. The page itself picks the
  // Super Admin vs Corp Admin checklist off the auth store.
  {
    path: '/guide',
    name: 'guide',
    component: () => import('@/features/guide/pages/GuidePage.vue'),
  },
  // FAQ-kb — knowledge base. READING is open to every signed-in admin (both roles):
  // it is product help, not tenant data, so it is absent from TENANT_ADMIN_ROUTES.
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/features/faq/pages/FaqPage.vue'),
  },
  {
    path: '/faq/:slug',
    name: 'faq-detail',
    component: () => import('@/features/faq/pages/FaqDetailPage.vue'),
  },
  {
    // AUTHORING is Super Admin only — the knowledge base is shared by every tenant,
    // so a Corp Admin must not be able to rewrite what other companies read.
    path: '/faq/admin',
    name: 'faq-admin',
    component: () => import('@/features/faq/pages/FaqAdminPage.vue'),
    meta: { superAdmin: true },
  },
  {
    // Declared BEFORE '/faq/admin/:id' so 'new' is never read as an article id.
    path: '/faq/admin/new',
    name: 'faq-create',
    component: () => import('@/features/faq/pages/FaqEditorPage.vue'),
    meta: { superAdmin: true },
  },
  {
    path: '/faq/admin/:id',
    name: 'faq-edit',
    component: () => import('@/features/faq/pages/FaqEditorPage.vue'),
    meta: { superAdmin: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/features/error/pages/ForbiddenPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/error/pages/NotFoundPage.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Màn tenant-scoped — Super Admin KHÔNG vào được (cần tenant context).
// Lưu ý: 'audit-log' KHÔNG nằm đây — Super Admin vẫn xem được (như 'settings' cũ).
const TENANT_ADMIN_ROUTES = ['corp-dashboard', 'organization', 'assets', 'loan-history', 'lost-asset-history', 'import-history', 'map-tracking', 'scenario-sets', 'alert-history', 'tag-history', 'card-management', 'tenant-corp-admins', 'zones', 'security-private-time', 'security-ip-restriction', 'security-device-restriction']

router.beforeEach((to) => {
  const auth = useAuthStore()
  const isPublic = to.meta.public === true

  if (!isPublic && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  // Web admin CHỈ cho Super Admin + Corp Admin (spec.md §58-59). End-user
  // (dept_manager/employee) dùng app mobile. Nếu token cũ còn trong storage khôi
  // phục session loại này → xóa và đưa về login (chặn ngoài luồng login()).
  if (!isPublic && auth.isAuthenticated && !auth.isSuperAdmin && !auth.isCorpAdmin) {
    auth.clear()
    return { name: 'login' }
  }
  // Landing sau login: Super Admin → Bảng Tổng quan Super; Corp Admin → Bảng Tổng quan Corp.
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: auth.isSuperAdmin ? 'dashboard' : auth.isCorpAdmin ? 'corp-dashboard' : 'organization' }
  }
  // Route chỉ dành cho Super Admin (vd Quản lý công ty)
  if (to.meta.superAdmin === true && !auth.isSuperAdmin) {
    return { name: 'organization' }
  }
  // Route chỉ dành cho Corp Admin tenant-scoped (vd quản lý admin, security settings).
  if (to.meta.corpAdmin === true && !auth.isCorpAdmin) {
    return { name: auth.isSuperAdmin ? 'dashboard' : 'organization' }
  }
  // Super Admin truy cập màn tenant-scoped → đưa về Quản lý công ty (tránh lỗi thiếu tenant)
  if (
    auth.isSuperAdmin &&
    typeof to.name === 'string' &&
    TENANT_ADMIN_ROUTES.includes(to.name)
  ) {
    return { name: 'dashboard' }
  }
  return true
})
